import { Stack } from '@mui/material';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useWatch } from 'react-hook-form';

import { SectionHeader } from '@/components/display/SectionHeader/SectionHeader';
import { SectionHeaderWithAccent } from '@/components/display/SectionHeaderWithAccent/SectionHeaderWithAccent';
import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout/SplitScreenLayout';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer } from '@/components/navigation/Footer';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { TextEditor } from '@/components/TextEditor/TextEditor';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { DocumentChatPane } from '@/modules/chat/components/DocumentChatPane';
import { useOptionalDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { DocumentViewerWithSearch } from '@/modules/documents/components/DocumentViewerWithSearch';
import { DocumentDetails_DocumentSummaryFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { useCreateSummary } from '@/modules/documents/hooks/useCreateSummary';
import { ConfirmDiscardChangesModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.components';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { useRequiredHouseholdId } from '@/modules/household/contexts/useRequiredHouseholdId';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useIsDocumentWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsDocumentWorkflowTrackerInProgress';
import { COLORS } from '@/styles/tokens/colors';
import {
  DocumentSummaryEntryMode,
  DocumentSummarySummaryFormat,
  WorkflowTrackerKind,
} from '@/types/schema';

import { useAnnotatedPDFViewerContext } from '../../pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { useDeleteSummary } from '../hooks/useDeleteSummary';
import { useSaveSummary } from '../hooks/useSaveSummary';
import { SelectableDocument } from '../ManageDocumentAmendments/types';
import { SummaryContentChangePlugin } from './components/SummaryContentChangePlugin';
import { ToggleChatButton } from './components/ToggleChatButton';
import { useEditSummaryTrowserData } from './hooks/useEditSummaryTrowserData';

interface SummaryFormShape {
  summaryContent: string;
  summaryTitle: string;
}

interface EditSummaryTrowserProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Fired after a summary is successfully saved (create or update) — awaited as
   * part of the submit, so it runs only once the write has completed — with the
   * values that were just saved. Distinct from `onClose`, which also fires on
   * cancel. Use it when a caller must react only to an actual save (e.g.
   * patching a presentation's summary so its slide re-paginates from the new
   * content without waiting on a backend-cached refetch).
   */
  onAfterSave?: (saved: {
    summary: string;
    displayName: string;
  }) => void | Promise<unknown>;
  documentId: string;
  // Only the fields the editor reads; a presentation passes its context summary,
  // which doesn't carry the full DocumentDetails_DocumentSummary fragment.
  documentSummary?: Pick<
    DocumentDetails_DocumentSummaryFragment,
    'id' | 'summary' | 'displayName'
  >;
  // additionalDocuments, when passed, renders a dropdown that allows the user to toggle between documents. This is
  // currently used to support multi-document summaries.
  additionalDocuments?: SelectableDocument[];
}

function SummaryEditorWrapper({
  children,
  hideBorder = false,
}: PropsWithChildren<{ hideBorder?: boolean }>) {
  const borderColor = COLORS.GRAY[200];

  return (
    <Stack
      color={COLORS.PRIMITIVES.BLACK}
      width="100%"
      lineHeight={1.5}
      textAlign="left"
      fontWeight={400}
      height="100%"
      overflow="hidden"
      sx={{
        borderTop: hideBorder ? 'none' : `1px solid ${borderColor}`,
        borderRight: hideBorder ? 'none' : `1px solid ${borderColor}`,
        borderBottom: hideBorder ? 'none' : `1px solid ${borderColor}`,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: 1.5,
        borderBottomRightRadius: 1.5,
      }}
    >
      {children}
    </Stack>
  );
}

function EditSummaryTrowserInner({
  isOpen,
  onClose,
  onAfterSave,
  documentId,
  documentSummary,
  additionalDocuments,
}: EditSummaryTrowserProps) {
  const canEditSummary = useCanEditHouseholdData();
  const householdId = useRequiredHouseholdId();
  const { showFeedback } = useFeedback();
  const { handleSubmit, setValue, formState, reset, control } =
    useFormContext<SummaryFormShape>();

  // Watch the form value to preserve edits when component remounts
  const watchedSummaryContent = useWatch({
    control,
    name: 'summaryContent',
  });
  const {
    documentId: viewerDocumentId,
    setOverrideDocumentId,
    setAnnotations,
    lastCitationParagraphIdsRef,
  } = useAnnotatedPDFViewerContext();
  const documentChatContext = useOptionalDocumentChatContext();

  const isAIEnabled = useAICapabilitiesEnabled();
  const isOcrInProgress = useIsDocumentWorkflowTrackerInProgress(
    documentId,
    WorkflowTrackerKind.DocumentOcr
  );
  const isDocumentChatEnabled =
    !!documentChatContext && isAIEnabled && !isOcrInProgress;

  // We don't want to use shouldBlockNavigation because we have a separate confirmation modal and that will block navigation on the underlying page
  const [contentIsDirty, setContentIsDirty] = useState(false);
  const summaryTitle = useWatch({ control, name: 'summaryTitle' });
  const summaryIsDirty =
    contentIsDirty || summaryTitle !== (documentSummary?.displayName || '');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Each open mounts a new form. Keep its baseline across editor remounts.
  const baselineRef = useRef<string | null>(null);

  const handleChangeContent = useCallback(
    (content: string, isDirty: boolean) => {
      setValue('summaryContent', content, { shouldDirty: isDirty });
      setContentIsDirty(isDirty);
    },
    [setValue]
  );

  const displayDocumentId = viewerDocumentId || documentId;

  const { document: selectedDocument } = useEditSummaryTrowserData(
    displayDocumentId,
    documentSummary?.id ?? null
  );

  // Clear the chat when the modal opens
  useEffect(() => {
    if (isOpen) {
      documentChatContext?.clearChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    reset();
    setOverrideDocumentId(null);
    setAnnotations(undefined);
    lastCitationParagraphIdsRef.current = [];
    onClose();
  }, [
    lastCitationParagraphIdsRef,
    onClose,
    reset,
    setAnnotations,
    setOverrideDocumentId,
  ]);

  const handleSelectDocument = useCallback(
    (documentId: string) => {
      setOverrideDocumentId(documentId);
      setAnnotations(undefined);
      documentChatContext?.clearChat();
    },
    [setOverrideDocumentId, setAnnotations, documentChatContext]
  );

  const displayDocumentFilePages = selectedDocument?.pdfFile?.pages;
  const fileId = selectedDocument?.pdfFile?.id;
  const { saveSummary } = useSaveSummary({ documentId });
  const { createSummary } = useCreateSummary({ documentId });
  const { deleteSummary, loading: deleteLoading } = useDeleteSummary({
    documentId,
    onSuccess: handleClose,
  });

  const onValidSubmission: SubmitHandler<SummaryFormShape> = async (values) => {
    // Don't save if summary content is empty
    if (!values.summaryContent.trim()) {
      showFeedback('Cannot save empty document summary');
      return;
    }

    const trimmedTitle = values.summaryTitle.trim();
    if (!trimmedTitle) {
      showFeedback('Document summary requires a title');
      return;
    }

    const result = !documentSummary
      ? // do NOT use viewerDocumentId here because we only want to save the
        // summary for the current document
        await createSummary({
          variables: {
            documentID: documentId,
            input: {
              documentv2ID: documentId,
              householdID: householdId,
              summary: values.summaryContent,
              summaryFormat: DocumentSummarySummaryFormat.Markdown,
              entryMode: DocumentSummaryEntryMode.User,
              displayName: trimmedTitle,
            },
          },
        })
      : await saveSummary({
          variables: {
            updateDocumentSummaryId: documentSummary.id,
            input: {
              summary: values.summaryContent,
              summaryFormat: DocumentSummarySummaryFormat.Markdown,
              entryMode: DocumentSummaryEntryMode.User,
              displayName: trimmedTitle,
            },
          },
        });

    // A mutation can settle with GraphQL errors (depending on errorPolicy)
    // without throwing. Only notify the caller once the write actually
    // succeeded, so consumers never react to a failed save — e.g. a
    // presentation patching its summary with edits that were never persisted.
    if (!result.data || result.errors?.length) {
      return;
    }

    // Hands back the saved values so consumers can patch their own data (e.g. a
    // presentation summary) without a refetch.
    await onAfterSave?.({
      summary: values.summaryContent,
      displayName: trimmedTitle,
    });
  };

  const onSubmit = handleSubmit(onValidSubmission);

  const handleRequestDiscardAndClose = useCallback(() => {
    if (summaryIsDirty) {
      setShowConfirmationModal(true);
      return;
    }
    handleClose();
  }, [handleClose, summaryIsDirty]);

  const handleCloseConfirmDiscardAndCloseModal = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setShowConfirmationModal(false);
      if (reason === 'confirm') {
        handleClose();
      }
    },
    [handleClose]
  );

  useSubmitSuccessHandler(handleClose);

  const handleChangeTitle = useCallback(
    (newTitle: string) => {
      const trimmedTitle = newTitle.replace(/&nbsp;/g, ' ').trim();
      setValue('summaryTitle', trimmedTitle);
    },
    [setValue]
  );

  const handleDeleteSummary = useCallback(() => {
    if (!documentSummary) return;
    void deleteSummary({
      variables: {
        id: documentSummary.id,
      },
    });
  }, [deleteSummary, documentSummary]);

  const sectionHeader = useMemo(() => {
    if (!canEditSummary) {
      return 'Document summary';
    }
    if (documentSummary) {
      return 'Edit document summary';
    }
    return 'Create document summary';
  }, [documentSummary, canEditSummary]);

  const headerContent = useMemo(() => {
    if (canEditSummary) {
      return (
        <EditableHeader
          value={summaryTitle}
          onChange={handleChangeTitle}
          onInput={handleChangeTitle}
          testId="EditSummaryTrowser-titleInput"
          disabled={!canEditSummary}
          placeholder="Summary title"
        />
      );
    }
    if (isDocumentChatEnabled) {
      return <SectionHeader title={sectionHeader} />;
    }
    return (
      <SectionHeaderWithAccent
        header={{
          heading: sectionHeader,
        }}
      />
    );
  }, [
    canEditSummary,
    sectionHeader,
    summaryTitle,
    handleChangeTitle,
    isDocumentChatEnabled,
  ]);

  // Use watched form value if it exists (preserves user edits when component remounts),
  // otherwise fall back to documentSummary?.summary
  const initialEditorValue = useMemo(() => {
    return watchedSummaryContent !== undefined
      ? watchedSummaryContent
      : documentSummary?.summary || '';
  }, [watchedSummaryContent, documentSummary?.summary]);

  const isChatOpen = documentChatContext?.isChatOpen ?? false;

  const documentContent = useMemo(() => {
    return (
      <Stack
        flex={1}
        p={2}
        gap={2}
        height="100%"
        // if the user can edit the summary, it will scroll inside the text editor, so we set overflow to hidden.
        // if the user cannot edit the summary, we want to scroll the entire summary, so we set overflow to auto.
        overflow={canEditSummary ? 'hidden' : 'auto'}
      >
        {headerContent}
        <TextEditor
          initialValue={initialEditorValue}
          initialValueKind="markdown"
          readOnly={!canEditSummary}
          TextEditorWrapper={SummaryEditorWrapper}
          innerSx={!canEditSummary ? { overflow: 'initial' } : {}}
          defaultAutofocusSelection={isChatOpen ? undefined : 'rootStart'}
          additionalPlugins={
            <SummaryContentChangePlugin
              baselineRef={baselineRef}
              onChange={handleChangeContent}
            />
          }
        />
      </Stack>
    );
  }, [
    canEditSummary,
    headerContent,
    initialEditorValue,
    isChatOpen,
    handleChangeContent,
  ]);

  return (
    <>
      <FullScreenModal isOpen={isOpen} onClose={handleClose}>
        <form onSubmit={onSubmit} style={{ height: '100%' }}>
          <FullScreenModalHouseholdChatHost>
            {({ householdChatPane, isHouseholdChatReplacementEnabled }) => (
              <SplitScreenLayout
                resizable
                leftContent={
                  <DocumentViewerWithSearch
                    key={fileId ?? displayDocumentId}
                    documentId={displayDocumentId}
                    source={fileId ? { kind: 'file', fileId } : null}
                    sx={{
                      borderRadius: 0,
                      border: 'none',
                      backgroundColor: COLORS.PRIMITIVES.WHITE,
                      boxShadow: 'none',
                      px: 0,
                    }}
                    documentChromeProps={{
                      shadowSx: {
                        boxShadow: 'none',
                      },
                    }}
                    pageWidthOffset={0}
                    filePages={displayDocumentFilePages || []}
                    associatedDocuments={additionalDocuments}
                    onSelectDocument={handleSelectDocument}
                  />
                }
                rightContent={documentContent}
                rightPane={
                  isHouseholdChatReplacementEnabled ? (
                    householdChatPane
                  ) : (
                    <DocumentChatPane
                      documentId={displayDocumentId}
                      enableCopyCitationPopover
                    />
                  )
                }
                footer={
                  <Footer
                    leftAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {canEditSummary && documentSummary ? (
                          <DeleteButton
                            size="sm"
                            variant="destructive-secondary"
                            onConfirmDelete={handleDeleteSummary}
                            promptDeleteText="Delete summary"
                            loading={deleteLoading}
                          />
                        ) : undefined}
                        {!isHouseholdChatReplacementEnabled &&
                          isDocumentChatEnabled && (
                            <ToggleChatButton
                              isChatOpen={documentChatContext.isChatOpen}
                              onOpenChat={documentChatContext.openChat}
                              onCloseChat={documentChatContext.closeChat}
                            />
                          )}
                      </Stack>
                    }
                    rightAction={
                      <Stack
                        spacing={1}
                        alignItems="center"
                        justifyContent="flex-end"
                        direction="row"
                      >
                        <Button
                          onClick={handleRequestDiscardAndClose}
                          variant="secondary"
                          size="sm"
                        >
                          Cancel
                        </Button>
                        {canEditSummary && (
                          <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            loading={formState.isSubmitting}
                          >
                            Save changes
                          </Button>
                        )}
                      </Stack>
                    }
                  />
                }
              />
            )}
          </FullScreenModalHouseholdChatHost>
        </form>
      </FullScreenModal>
      <ConfirmDiscardChangesModal
        isOpen={showConfirmationModal}
        onClose={handleCloseConfirmDiscardAndCloseModal}
      />
    </>
  );
}

export function EditSummaryTrowser(props: EditSummaryTrowserProps) {
  const { documentSummary } = props;

  const formMethods = useForm<SummaryFormShape>({
    defaultValues: {
      summaryContent: documentSummary?.summary || '',
      summaryTitle: documentSummary?.displayName || '',
    },
  });

  return (
    <FormProvider {...formMethods}>
      <EditSummaryTrowserInner {...props} />
    </FormProvider>
  );
}
